import React from 'react';
import { DataPackage, normalizePackageDescription } from '../../services/dataPackagesService';

interface DataPackageModalProps {
  dataPackage: DataPackage | null;
  isOpen: boolean;
  onClose: () => void;
  hasAccess?: boolean;
  onOpen?: () => void;
}

const DataPackageModal: React.FC<DataPackageModalProps> = ({ dataPackage, isOpen, onClose, hasAccess, onOpen }) => {
  if (!isOpen || !dataPackage) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        position: 'relative'
      }} onClick={(e) => e.stopPropagation()}>
        
        {/* Кнопка закрытия */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f0f0f0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          ×
        </button>

        {/* Заголовок */}
        <h2 style={{
          fontSize: '24px',
          fontWeight: '600',
          color: '#1a237e',
          margin: '0 0 16px 0',
          fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
        }}>
          {dataPackage.name}
        </h2>

        {/* Описание */}
        <p style={{
          fontSize: '16px',
          color: '#555',
          lineHeight: '1.5',
          margin: '0 0 24px 0'
        }}>
          {normalizePackageDescription(dataPackage.description)}
        </p>



        {/* Распределение компаний по секторам */}
        {(() => {
          const sectorStats = dataPackage.companies_data.reduce((acc, company) => {
            if (!acc[company.sector]) {
              acc[company.sector] = 0;
            }
            acc[company.sector]++;
            return acc;
          }, {} as Record<string, number>);

          const totalCompanies = dataPackage.companies_data.length;
          const sectorEntries = Object.entries(sectorStats).sort((a, b) => b[1] - a[1]);

          return (
            <div style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '24px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1a237e',
                margin: '0 0 16px 0'
              }}>
                Распределение компаний по секторам
              </h3>
              
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{
                    borderBottom: '2px solid #e0e0e0'
                  }}>
                    <th style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: '#1a237e',
                      width: '200px'
                    }}>
                      Сектор
                    </th>
                    <th style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: '#1a237e',
                      width: '100px'
                    }}>
                      Количество
                    </th>
                    <th style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: '#1a237e'
                    }}>
                      Процент
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sectorEntries.map(([sector, count], index) => {
                    const percentage = ((count / totalCompanies) * 100).toFixed(1);
                    
                    return (
                      <tr key={index} style={{
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <td style={{
                          padding: '12px 8px',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          fontWeight: '500',
                          color: '#333'
                        }}>
                          {sector}
                        </td>
                        <td style={{
                          padding: '12px 8px',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          color: '#666'
                        }}>
                          {count}
                        </td>
                        <td style={{
                          padding: '12px 8px',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          color: '#666'
                        }}>
                          {percentage}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* Распределение компаний по уровням надежности */}
        {(() => {
          const reliabilityLevels = [
            { level: 'Excellent', color: '#073763', description: 'Очень высокая достоверность (Excellent)', min: 0.8, max: 1.0 },
            { level: 'High', color: '#1155cc', description: 'Высокая достоверность (High)', min: 0.5, max: 0.8 },
            { level: 'Moderate', color: '#6d9eeb', description: 'Умеренная достоверность (Moderate)', min: 0.3, max: 0.5 },
            { level: 'Low', color: '#999999', description: 'Информация требует проверки (Low)', min: 0.0, max: 0.3 }
          ];

          const reliabilityStats = dataPackage.companies_data.reduce((acc, company) => {
            const score = company.reliability_score;
            const level = reliabilityLevels.find(l => score > l.min && score <= l.max);
            if (level) {
              if (!acc[level.level]) {
                acc[level.level] = { count: 0, color: level.color, description: level.description };
              }
              acc[level.level].count++;
            }
            return acc;
          }, {} as Record<string, { count: number; color: string; description: string }>);

          const totalCompanies = dataPackage.companies_data.length;

          return (
            <div style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '24px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1a237e',
                margin: '0 0 16px 0'
              }}>
                Распределение компаний по уровням надежности данных
              </h3>
              <div style={{
                fontSize: '14px',
                color: '#1a237e',
                marginBottom: '16px',
                fontWeight: '500',
                backgroundColor: '#e3f2fd',
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #bbdefb',
                display: 'inline-block'
              }}>
                <i className="fas fa-calendar-alt" style={{ marginRight: '8px' }}></i>
                Актуально на {new Date().toLocaleDateString('ru-RU')}
              </div>
              
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{
                    borderBottom: '2px solid #e0e0e0'
                  }}>
                    <th style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: '#1a237e',
                      width: '60px'
                    }}>
                      Цвет
                    </th>
                    <th style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: '#1a237e',
                      width: '200px'
                    }}>
                      Уровень надежности
                    </th>
                    <th style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: '#1a237e',
                      width: '100px'
                    }}>
                      Количество
                    </th>
                    <th style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: '#1a237e'
                    }}>
                      Процент
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reliabilityLevels.map((level, index) => {
                    const count = reliabilityStats[level.level]?.count || 0;
                    const percentage = totalCompanies > 0 ? ((count / totalCompanies) * 100).toFixed(1) : '0.0';
                    
                    return (
                      <tr key={index} style={{
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <td style={{
                          padding: '12px 8px',
                          verticalAlign: 'middle',
                          textAlign: 'center'
                        }}>
                          <div style={{
                            width: '20px',
                            height: '20px',
                            backgroundColor: level.color,
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            margin: '0 auto'
                          }}></div>
                        </td>
                        <td style={{
                          padding: '12px 8px',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          fontWeight: '500',
                          color: '#333'
                        }}>
                          {level.description}
                        </td>
                        <td style={{
                          padding: '12px 8px',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          color: '#666'
                        }}>
                          {count}
                        </td>
                        <td style={{
                          padding: '12px 8px',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          color: '#666'
                        }}>
                          {percentage}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {/* Информационная панель об уровнях надежности */}
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '16px',
                borderRadius: '8px',
                marginTop: '16px',
                fontSize: '13px',
                border: '1px solid #e9ecef'
              }}>
                <h4 style={{ 
                  margin: '0 0 12px 0', 
                  color: '#1a237e',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
                  Уровни надежности данных
                </h4>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr', 
                  gap: '16px',
                  fontSize: '12px',
                  lineHeight: '1.4'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '12px',
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: '#073763',
                      borderRadius: '3px',
                      flexShrink: 0,
                      marginTop: '2px'
                    }}></div>
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '4px', color: '#333' }}>
                        Очень высокая достоверность (Excellent)
                      </div>
                      <div style={{ color: '#666', fontSize: '11px' }}>
                        Данные предоставлены компанией референтной группы
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '12px',
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: '#1155cc',
                      borderRadius: '3px',
                      flexShrink: 0,
                      marginTop: '2px'
                    }}></div>
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '4px', color: '#333' }}>
                        Высокая достоверность (High)
                      </div>
                      <div style={{ color: '#666', fontSize: '11px' }}>
                        Данные получены в ходе полевых исследований / верифицированы командой аналитики datacircle
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '12px',
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: '#6d9eeb',
                      borderRadius: '3px',
                      flexShrink: 0,
                      marginTop: '2px'
                    }}></div>
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '4px', color: '#333' }}>
                        Умеренная достоверность (Moderate)
                      </div>
                      <div style={{ color: '#666', fontSize: '11px' }}>
                        Данные предоставлены сотрудником компании / получены из открытых источников / являются результатом работы регрессионной модели datacircle (подтвержденная точность не менее 85%)
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '12px',
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: '#999999',
                      borderRadius: '3px',
                      flexShrink: 0,
                      marginTop: '2px'
                    }}></div>
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '4px', color: '#333' }}>
                        Информация требует проверки (Low)
                      </div>
                      <div style={{ color: '#666', fontSize: '11px' }}>
                        Данные являются результатом работы регрессионной модели datacircle c подтвержденной точностью менее 85% / получены из иных источников
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Должности */}
        {dataPackage.positions && dataPackage.positions.length > 0 && (
          <div style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '24px'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1a237e',
              margin: '0 0 16px 0'
            }}>
              Должности в пакете
            </h3>
            
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              {dataPackage.positions.map((position, index) => (
                <span
                  key={index}
                  style={{
                    backgroundColor: '#e3f2fd',
                    color: '#1a237e',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '500',
                    border: '1px solid #bbdefb'
                  }}
                >
                  {position}
                </span>
              ))}
            </div>
          </div>
        )}



        {hasAccess && (
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => { onClose(); onOpen?.(); }}
              style={{
                backgroundColor: '#1a237e',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 32px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#283593'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1a237e'; }}
            >
              Открыть аналитику
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataPackageModal;


